/**
 * T058 (catalogue-admin-screens, base-prices). Reads `GET /skus/{skuId}/base-prices`; the write
 * action re-renders the stored, API-rounded rates rather than what was typed (FR-005) — see the
 * submit handler's comment.
 *
 * The row set is fixed to the SKU's existing sellable units (that set is edited on `Units.tsx`, not
 * here), so there is no add/remove chrome. The closest template match for a `useFieldArray` list of
 * one-input-per-row with rows that can't be added or removed is
 * `vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/product/create-edit/product-variants.tsx`
 * (confirmed on disk) — this file is that pattern with the "Add Variant" button and the per-row
 * trash icon deleted, since neither action applies to a fixed row set. The form shell
 * (`formCard`/`formSections`/`FormFooter`) and the `RecordSummary` strip are the ones every other
 * edit screen uses.
 *
 * 2026-09-29 (owner: "too basic and messy, say what this is for"): one aligned table — unit, rate,
 * GST treatment, what is saved now — and a note on what a base price is, where it goes next (every
 * other tier is generated from it) and what each GST choice does. Every figure shown is the API's:
 * the "saved now" column is the stored, rounded rate, never the typed one.
 */
import { useState } from "react";
import { Link, useLoaderData, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useGoBack } from "../../../lib/navigation";
import { skuPath } from "../../../lib/paths";
import {
  PiCurrencyInrDuotone as PriceIcon,
  PiInfoDuotone as InfoIcon,
  PiPencilSimpleDuotone as EditIcon,
  PiSlidersHorizontalDuotone as UnitsIcon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { PageHeader, EmptyState, ErrorSurface, FormGroup, cn } from "@steelyard/theme";
import { SelectField } from "../../../components/select-field";
import { SectionTitle } from "../../../components/section-title";
import { RecordSummary } from "../../../components/detail-parts";
import { Alert, Badge, Button, Input, Text } from "rizzui";
import { byKey, getBasePrices, getSku, getSkuBySlug, setBasePrices, type BasePricesView, type GstTreatment } from "../../../lib/services/catalog";
import { formatMoney } from "../../../lib/format";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { can } from "../../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../../types";

export async function basePricesLoader({ params, request }: LoaderFunctionArgs) {
  const sku = await byKey(request, params.slug!, getSku, getSkuBySlug, skuPath);
  const basePrices = await getBasePrices(sku.id);
  if (!basePrices) throw new Error("The API returned no body for this SKU's base prices.");
  // Only for naming the stocking unit in each row's "1 PCS = 7.4 KGS".
  const stockingCode = sku.stockingUnitCode ?? sku.units.find((u) => u.unitId === sku.stockingUnitId)?.unitCode ?? null;
  return { basePrices, stockingCode, skuHref: skuPath(sku.slug) };
}

/** "" = not chosen: the API applies the unit's configured default (D-04). */
type RowValues = { unitId: string; unitCode: string; rate: string; gstTreatment: GstTreatment | "" };
type FormValues = { prices: RowValues[] };

function rowsFrom(basePrices: BasePricesView): RowValues[] {
  return basePrices.prices.map((p) => ({ unitId: p.unitId, unitCode: p.unitCode, rate: p.rate == null ? "" : String(p.rate), gstTreatment: p.gstTreatment ?? "" }));
}

const GST_LABEL: Record<GstTreatment, string> = { inclusive: "incl. GST", exclusive: "+ GST" };

/** The one grid every row and the header share, so the columns line up. */
const GRID = "grid grid-cols-[minmax(12rem,1.3fr)_minmax(13rem,1.2fr)_minmax(10rem,0.9fr)_minmax(8rem,0.8fr)] items-center gap-x-4";

export default function BasePrices() {
  const { basePrices, stockingCode, skuHref } = useLoaderData() as Awaited<ReturnType<typeof basePricesLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const goBack = useGoBack(skuHref);
  const [error, setError] = useState<unknown>(null);
  const {
    control,
    register,
    handleSubmit,
    setError: setFieldError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { prices: rowsFrom(basePrices) } });
  const { fields } = useFieldArray({ control, name: "prices" });

  if (!can(permissionState, "pricing.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this SKU's prices" />;
  }
  const canWrite = can(permissionState, "pricing.base.administer");

  const rowInfo = new Map(basePrices.prices.map((p) => [p.unitId, p]));

  const submit = handleSubmit(async (values) => {
    clearErrors();
    setError(null);
    let hasError = false;
    const payload: { unitId: string; rate: number; gstTreatment?: GstTreatment }[] = [];

    values.prices.forEach((row, index) => {
      const typed = row.rate.trim();
      if (!typed) return; // an admin not touching this unit doesn't need to resubmit it — dropped silently
      const rate = Number(typed);
      if (!Number.isFinite(rate) || rate <= 0) {
        setFieldError(`prices.${index}.rate`, { message: "Enter a rate greater than 0" });
        hasError = true;
        return;
      }
      payload.push({ unitId: row.unitId, rate, gstTreatment: row.gstTreatment || undefined });
    });

    if (hasError) return;
    if (payload.length === 0) {
      setFieldError("root", { message: "Enter a rate on at least one row to save." });
      return;
    }

    try {
      await setBasePrices(basePrices.skuId, payload);
      // Back to the screen this was opened from; reopening reads the stored, rounded rates (FR-005)
      // and server-assigned gstTreatment fresh from the loader rather than trusting what was typed.
      toast.success("Prices saved");
      goBack();
    } catch (err) {
      setError(err);
      toast.error("Could not save these prices");
    }
  });

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={`${basePrices.skuCode} — Base Prices`}
        breadcrumb={[
          { name: "Home", href: "/" },
          { name: "Catalogue · SKUs", href: "/catalogue/skus" },
          { name: basePrices.skuCode, href: skuHref },
          { name: "Base prices" },
        ]}
      />

      <RecordSummary
        code={basePrices.skuCode}
        links={
          <>
            <Link to={skuHref}>
              <Button as="span" variant="outline" size="sm">
                <EditIcon aria-hidden className="me-1.5 size-4" />
                Edit SKU
              </Button>
            </Link>
            <Link to={`${skuHref}/units`}>
              <Button as="span" variant="outline" size="sm">
                <UnitsIcon aria-hidden className="me-1.5 size-4" />
                Sellable units
              </Button>
            </Link>
          </>
        }
      />

      {error ? <ErrorSurface error={error} action="save these prices" /> : null}

      <form onSubmit={submit} className={formCard}>
        <div className={formSections}>
          <FormGroup
            title={<SectionTitle icon={PriceIcon}>Base prices</SectionTitle>}
            description="The Default Tier's price for each unit this SKU is sold in."
          >
            <Alert color="info" variant="flat" icon={<InfoIcon aria-hidden className="size-5" />} className="col-span-full">
              <ul className="list-disc space-y-1 ps-4 text-sm">
                <li>
                  A base price is what a guest, or a customer on the <strong>Default Tier</strong>, pays for one unit.
                </li>
                <li>
                  Every other tier&apos;s prices are generated from these by its discount rules. Changing a base price marks
                  those tier prices <Link to="/reports/staleness" className="font-medium underline">stale</Link> until they are re-applied.
                </li>
                <li>Each unit has its own rate — the piece price is not worked out from the per-kilogram rate.</li>
                <li>
                  <strong>GST:</strong> Inclusive — the rate already contains GST. Exclusive — GST is added on top.
                  Unit default — Infra Junction applies its configured default (normally inclusive for pieces, exclusive for
                  weight and length units).
                </li>
                <li>Rates are stored to 2 decimals. Leave a rate blank to leave that unit as it is.</li>
              </ul>
            </Alert>

            {fields.length === 0 ? (
              <div className="col-span-full">
                <EmptyState
                  title="No sellable units yet"
                  action={
                    <Link to={`${skuHref}/units`}>
                      <Button as="span" variant="outline" size="sm">
                        Set up sellable units
                      </Button>
                    </Link>
                  }
                >
                  A price is set per sellable unit, and this SKU has none. Add its units first.
                </EmptyState>
              </div>
            ) : (
              <div className="col-span-full flex flex-col gap-3">
                {errors.root?.message ? (
                  <p role="alert" className="text-sm text-red-dark">
                    {errors.root.message}
                  </p>
                ) : null}

                <div className="overflow-x-auto">
                  <div className="min-w-[46rem] rounded-lg border border-muted">
                    <div
                      aria-hidden
                      className={cn(GRID, "rounded-t-lg border-b border-muted bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500")}
                    >
                      <span>Unit</span>
                      <span>Rate</span>
                      <span>GST</span>
                      <span className="text-end">Saved now</span>
                    </div>

                    {fields.map((item, index) => {
                      const info = rowInfo.get(item.unitId);
                      const rateError = errors.prices?.[index]?.rate?.message;
                      return (
                        <div key={item.id} className={cn(GRID, "border-b border-muted px-4 py-3 last:border-b-0")}>
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="flex flex-wrap items-center gap-1.5">
                              <Badge variant="flat" color="secondary" rounded="md" size="sm" className="font-mono">
                                {item.unitCode}
                              </Badge>
                              {info?.isPrimary ? (
                                <Badge variant="flat" color="primary" rounded="md" size="sm">
                                  Primary
                                </Badge>
                              ) : null}
                            </span>
                            {info && stockingCode ? (
                              <Text className="font-mono text-xs text-gray-500">
                                {item.unitCode === stockingCode
                                  ? "Stocking unit"
                                  : `1 ${item.unitCode} = ${info.conversionToStocking} ${stockingCode}`}
                              </Text>
                            ) : null}
                          </div>

                          <div className="flex min-w-0 flex-col gap-1">
                            <Input
                              aria-label={`Rate per ${item.unitCode}`}
                              id={`base-price-${item.unitId}`}
                              inputMode="decimal"
                              prefix="₹"
                              suffix={<span className="text-xs text-gray-500">per {item.unitCode}</span>}
                              inputClassName="font-mono"
                              disabled={!canWrite}
                              error={rateError ? " " : undefined}
                              {...register(`prices.${index}.rate`)}
                            />
                            {rateError ? (
                              <p role="alert" className="text-[13px] text-red">
                                {rateError}
                              </p>
                            ) : null}
                          </div>

                          {/* D-04: GST treatment is configurable per price field. */}
                          <SelectField
                            label={`GST treatment for ${item.unitCode}`}
                            hideLabel
                            id={`base-gst-${item.unitId}`}
                            disabled={!canWrite}
                            {...register(`prices.${index}.gstTreatment`)}
                          >
                            <option value="" data-choice>
                              Unit default
                            </option>
                            <option value="inclusive">Inclusive</option>
                            <option value="exclusive">Exclusive</option>
                          </SelectField>

                          <div className="text-end">
                            {info?.priced && info.rate != null ? (
                              <>
                                <span className="font-semibold tabular-nums text-gray-900">{formatMoney(info.rate)}</span>
                                {info.gstTreatment ? (
                                  <span className="block text-xs text-gray-500">{GST_LABEL[info.gstTreatment]}</span>
                                ) : null}
                              </>
                            ) : (
                              <Badge variant="flat" color="warning" rounded="md" size="sm">
                                Not priced yet
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </FormGroup>
        </div>

        {canWrite && fields.length > 0 ? (
          <FormFooter
            className={formCardFooter}
            submitBtnText={isSubmitting ? "Saving…" : "Save prices"}
            isLoading={isSubmitting}
            altBtnText="Cancel"
            handleAltBtn={goBack}
          />
        ) : null}
      </form>
    </div>
  );
}
